import axios from 'axios';
import API_BASE_URL from 'config/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, result) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(result);
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // Skip refresh logic for public/auth endpoints — these are called before the user
      // is logged in (e.g. on the login page) and should just fail silently.
      const publicEndpoints = ['/api/admin/verify', '/api/admin/profile', '/api/admin/refresh'];
      const isPublicEndpoint = publicEndpoints.some(
        (ep) => originalRequest.url && originalRequest.url.includes(ep)
      );
      if (isPublicEndpoint) {
        // If the refresh endpoint itself failed, clear stored user.
        if (originalRequest.url && originalRequest.url.includes('/api/admin/refresh')) {
          try { localStorage.removeItem('user'); } catch (e) {}
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => apiClient(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // call refresh endpoint using plain axios to avoid interceptor loop
        // add debug log to help diagnose CORS / cookie issues
        // eslint-disable-next-line no-console
        console.debug('Attempting token refresh:', `${API_BASE_URL}/api/admin/refresh`);
        const res = await axios.post(`${API_BASE_URL}/api/admin/refresh`, {}, { withCredentials: true });
        // eslint-disable-next-line no-console
        console.debug('Refresh response:', res.status, res.data);
        // if refresh succeeded, retry original requests
        processQueue(null, true);
        isRefreshing = false;
        return apiClient(originalRequest);
      } catch (err) {
        // refresh failed - inspect message and force logout
        processQueue(err, null);
        isRefreshing = false;
        try {
          // remove stored user and redirect to login
          localStorage.removeItem('user');
        } catch (e) {}
        // persist server response for post-mortem debugging
        try {
          const payload = { status: err.response?.status, data: err.response?.data || err.message };
          localStorage.setItem('refreshError', JSON.stringify(payload));
        } catch (e) {}
        // eslint-disable-next-line no-console
        console.warn('Refresh failed:', err.response?.status, err.response?.data || err.message);
        // Only redirect to /login if not already there to prevent an infinite reload loop
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location = '/login';
        }
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
